import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { Briefcase, Users, ShieldCheck, RefreshCw, Zap, Clock } from "lucide-react";
import { toast } from "react-hot-toast";

const API_BASE = process.env.REACT_APP_API_BASE;

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalJobs: 0,
    totalWorkers: 0,
    pendingApprovals: 0,
  });

  const [loadingRefund, setLoadingRefund] = useState(false);
  const [loadingAutoRelease, setLoadingAutoRelease] = useState(false);
  const [loadingRideAuto, setLoadingRideAuto] = useState(false);

  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API_BASE}/admin/stats`);
        setStats(res.data || {});
      } catch (err) {
        console.error("❌ Failed to fetch stats:", err);
      }
    };
    fetchStats();
  }, []);

  // Refund Expired Jobs
  const handleRefundSweep = async () => {
    setLoadingRefund(true);
    try {
      const res = await axios.post(`${API_BASE}/payment/refund-expired-jobs`);
      const results = res.data.results || [];

      if (results.length === 0) {
        toast("ℹ️ No expired jobs found.");
      } else {
        const refunded = results.filter((r) => r.refundId).length;
        const cancelled = results.filter((r) => !r.refundId && !r.error).length;
        const failed = results.filter((r) => r.error).length;

        toast.success(
          `Refund Sweep: ${refunded} refunded, ${cancelled} cancelled, ${failed} failed.`
        );
      }

    } catch (err) {
      console.error("Refund sweep failed:", err);
      toast.error("Refund sweep failed");
    } finally {
      setLoadingRefund(false);
    }
  };

  // Auto Release
  const handleAutoRelease = async () => {
    setLoadingAutoRelease(true);
    try {
      const res = await axios.post(`${API_BASE}/admin/run-auto-release`);
      toast.success(res.data.message || "Auto-release executed!");
    } catch (err) {
      console.error("Auto release failed:", err);
      toast.error("Auto-release failed");
    } finally {
      setLoadingAutoRelease(false);
    }
  };

  // Ride Auto Complete
  const handleRideAutoComplete = async () => {
    setLoadingRideAuto(true);
    try {
      const res = await axios.post(`${API_BASE}/admin/rides/auto-complete`);
      toast.success(res.data.message || "Ride auto-complete executed!");
    } catch (err) {
      console.error("Ride auto-complete failed:", err);
      toast.error("Ride auto-complete failed");
    } finally {
      setLoadingRideAuto(false);
    }
  };

  return (
    <div className="flex">
      <Sidebar />
      <main className="ml-0 md:ml-64 w-full bg-gray-100 min-h-screen p-6 md:p-10">

        <h1 className="text-3xl font-extrabold text-gray-800 mb-8">
          Welcome back, <span className="text-indigo-600">Super Admin 👑</span>
        </h1>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {/* Total Jobs */}
          <div className="rounded-2xl shadow p-6 bg-blue-50">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-700">Total Jobs</h2>
              <Briefcase className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-4xl font-bold text-blue-600">{stats.totalJobs}</p>
          </div>

          {/* Total Workers */}
          <div className="rounded-2xl shadow p-6 bg-green-50">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-700">Total Workers</h2>
              <Users className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-4xl font-bold text-green-600">{stats.totalWorkers}</p>
          </div>

          {/* Pending Approvals */}
          <div className="rounded-2xl shadow p-6 bg-yellow-50">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-700">Pending Approvals</h2>
              <ShieldCheck className="w-6 h-6 text-yellow-600" />
            </div>
            <p className="text-4xl font-bold text-yellow-600">{stats.pendingApprovals}</p>
          </div>
        </div>

        {/* Refund Sweep */}
        <div className="bg-white rounded-2xl shadow p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">💸 Refund Expired Jobs</h2>
          <button
            onClick={handleRefundSweep}
            disabled={loadingRefund}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-70"
          >
            <RefreshCw className="w-5 h-5" />
            {loadingRefund ? "Processing..." : "Run Refund Sweep"}
          </button>
          <p className="text-sm text-gray-500 mt-2">
            Refunds expired & unassigned jobs automatically.
          </p>
        </div>

        {/* Auto-Release */}
        <div className="bg-white rounded-2xl shadow p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">⚡ Auto-Release Jobs</h2>
          <button
            onClick={handleAutoRelease}
            disabled={loadingAutoRelease}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-70"
          >
            <Zap className="w-5 h-5" />
            {loadingAutoRelease ? "Releasing..." : "Run Auto-Release"}
          </button>
          <p className="text-sm text-gray-500 mt-2">
            Releases funds after 48 hours automatically.
          </p>
        </div>

        {/* Ride Auto Complete */}
        <div className="bg-white rounded-2xl shadow p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">🕒 Ride Auto-Complete</h2>
          <button
            onClick={handleRideAutoComplete}
            disabled={loadingRideAuto}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-semibold disabled:opacity-70"
          >
            <Clock className="w-5 h-5" />
            {loadingRideAuto ? "Processing..." : "Run Ride Auto-Complete"}
          </button>
          <p className="text-sm text-gray-500 mt-2">
            Completes rides & releases payouts after 48 hours.
          </p>
        </div>

      </main>
    </div>
  );
};

export default AdminDashboard;
